<template>
  <div class="blog-post-container container mb-4">
    <!-- The blog post -->
    <div class="blog-post">
      <BaseTitle>{{ blogPost.title }}</BaseTitle>
      <div class="published-at-by">
        <BaseText class="nowrap">{{
          $t("blog.published-on", { date: $d(new Date(blogPost.publishedAt)) })
        }}</BaseText>

        <div v-if="blogPost.user" class="nowrap">
          <BaseText>{{ $t("blog.written-by") }}</BaseText>
          <div v-if="blogPost.user.fileUrl" class="avatar ml-0-5">
            <BaseImageExternal
              :src="blogPost.user.fileUrl"
              class="avatar-image"
            />
          </div>
          <BaseText
            >&nbsp;{{
              `${blogPost.user.firstName} ${blogPost.user.lastName}`
            }}</BaseText
          >
        </div>
      </div>
      <BaseCustomHTMLContent
        v-if="blogPost.content"
        class="blog-post-content"
        :custom-html-content="blogPost.content"
      ></BaseCustomHTMLContent>

      <!-- Blog category buttons -->
      <div v-if="blogPost.categories.length" class="mt-1-5">
        <BaseText>{{ $t("blog.more-like-this") }}</BaseText>
        <div class="category-buttons">
          <BaseButtonLink
            v-for="category in blogPost.categories"
            :key="category.id"
            :to="
              localePath({
                name: 'blog',
                query: { c: category.slug },
              })
            "
            class="button-extra-small"
          >
            {{ category.name }}
          </BaseButtonLink>
        </div>
      </div>

      <!-- Social share buttons -->
      <SocialShareButtons
        :share-text="shareText"
        :share-url="shareUrl"
        class="mt-1 mb-1"
      />
    </div>

    <!-- Right side with recent posts -->
    <BlogRecentPosts :current-post-path="blogPost.path" class="recent-posts" />
  </div>
</template>

<script setup lang="ts">
import { type WebsitePagePost } from "~/graphql/generated";

const props = defineProps<{
  blogPost: WebsitePagePost;
}>();

const localePath = useLocalePath();

const { t } = useI18n();

const shareUrl = useRequestURL();
const shareText = computed(() => {
  return t("blog.share-text", {
    title: props.blogPost.title,
    url: shareUrl,
  });
});
</script>

<style lang="scss" scoped>
.blog-post-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;

  @include for-small-screen {
    flex-direction: column;
    gap: 1rem;
  }

  .blog-post {
    flex: 1;
    padding: 0 0 1.5rem 0;

    @include for-medium-screen-up {
      padding: 0 1.5rem 0 0;
      border-right: 1px solid $light-80;
    }
  }

  .category-buttons {
    margin: 0.5rem 0;
    display: flex;
    gap: 0.25rem;
  }

  $avatar-size: 24px;

  .published-at-by {
    margin: 0.5rem 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0 1rem;
    line-height: $avatar-size; // Same as avatar height

    .nowrap {
      display: flex;
      flex-wrap: nowrap;
      text-wrap: nowrap;
    }

    .avatar {
      border-radius: 50%;
      overflow: hidden;

      width: $avatar-size;
      height: $avatar-size;
      display: flex;
      align-items: center;
      justify-content: center;

      .avatar-image {
        height: unset;
        width: unset;
        min-width: 100%;
        min-height: 100%;
        object-fit: cover;
      }
    }
  }
}
</style>
